/**
 * SmartBooker · Voice-over uploader
 * Lets the user pick a recorded audio file (mp3 / m4a / wav) and merges it
 * server-side with the existing 90s silent MP4 via /api/video-demo/with-voice.
 * Returns the merged MP4 as an automatic browser download.
 */
import React, { useRef, useState } from "react";
import { Sparkles, Upload, Loader2, Download, Mic, Check, AlertCircle, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";
const ALLOWED = [".mp3", ".m4a", ".wav", ".aac", ".ogg", ".opus", ".webm", ".flac"];

const VideoDemoVoiceOverPage = () => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | uploading | done | error
  const [error, setError] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (!ALLOWED.some(ext => lower.endsWith(ext))) {
      setError(`Formato non supportato. Usa: ${ALLOWED.join(", ")}`);
      setFile(null);
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      setError("File troppo grande (max 50MB)");
      setFile(null);
      return;
    }
    setError(null);
    setFile(f);
    setPhase("idle");
    setDownloadUrl(null);
  };

  const onSubmit = async () => {
    if (!file) return;
    setPhase("uploading");
    setError(null);
    try {
      const fd = new FormData();
      fd.append("audio", file);
      const r = await fetch(`${API}/api/video-demo/with-voice`, {
        method: "POST",
        body: fd,
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      setDownloadUrl(url);
      // auto-trigger download
      const a = document.createElement("a");
      a.href = url;
      a.download = "smartbooker-demo-90s-voce.mp4";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setPhase("done");
    } catch (e) {
      setError(String(e.message || e));
      setPhase("error");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6" data-testid="voiceover-page">
      <div className="w-full max-w-xl">
        <Link
          to="/video-demo"
          className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-6 transition-colors"
          data-testid="voiceover-back"
        >
          <ArrowLeft className="w-4 h-4" /> Torna al video demo
        </Link>

        <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 border border-white/10 p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shadow-lg">
              <Mic className="w-4 h-4 text-white" />
            </div>
            <span className="text-emerald-400 text-[10px] font-bold uppercase tracking-[0.18em]">
              <Sparkles className="w-3 h-3 inline mr-1" /> Voice-over · Studio
            </span>
          </div>
          <h1 className="text-white font-black text-3xl leading-tight">
            Aggiungi la tua voce<br/>al video demo.
          </h1>
          <p className="text-white/60 text-sm mt-3 leading-relaxed">
            Carica un file audio (mp3, m4a, wav). Lo unisco al video da 90 secondi e
            ti ridò l'MP4 finale, pronto da inviare ai prospect su WhatsApp.
          </p>

          {/* FILE PICKER */}
          <div className="mt-6">
            <input
              ref={inputRef}
              type="file"
              accept={ALLOWED.join(",")}
              onChange={onPick}
              className="hidden"
              data-testid="voiceover-file-input"
            />
            <button
              onClick={() => inputRef.current?.click()}
              disabled={phase === "uploading"}
              data-testid="voiceover-pick-btn"
              className={`w-full rounded-2xl border-2 border-dashed p-8 transition-colors ${
                file
                  ? "border-emerald-400/40 bg-emerald-400/5 hover:bg-emerald-400/10"
                  : "border-white/15 bg-white/5 hover:bg-white/10"
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                {file ? (
                  <>
                    <Check className="w-7 h-7 text-emerald-400" />
                    <p className="text-white font-bold text-sm" data-testid="voiceover-file-name">
                      {file.name}
                    </p>
                    <p className="text-white/50 text-[11px]">
                      {(file.size / 1024 / 1024).toFixed(1)} MB · clicca per cambiarlo
                    </p>
                  </>
                ) : (
                  <>
                    <Upload className="w-7 h-7 text-white/60" />
                    <p className="text-white font-semibold text-sm">
                      Clicca per scegliere il tuo file audio
                    </p>
                    <p className="text-white/50 text-[11px]">
                      mp3, m4a, wav, aac · max 50 MB
                    </p>
                  </>
                )}
              </div>
            </button>
          </div>

          {error && (
            <div
              className="mt-4 flex items-start gap-2 rounded-xl bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-rose-300 text-xs"
              data-testid="voiceover-error"
            >
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* SUBMIT */}
          <button
            onClick={onSubmit}
            disabled={!file || phase === "uploading"}
            data-testid="voiceover-submit-btn"
            className={`w-full mt-5 rounded-full py-3.5 font-bold text-sm flex items-center justify-center gap-2 transition-all ${
              !file || phase === "uploading"
                ? "bg-white/10 text-white/40 cursor-not-allowed"
                : "bg-gradient-to-r from-emerald-400 to-cyan-500 text-slate-950 hover:shadow-2xl hover:scale-[1.02]"
            }`}
          >
            {phase === "uploading" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Unione audio + video in corso...
              </>
            ) : phase === "done" ? (
              <>
                <Check className="w-4 h-4" />
                Pronto · Scaricato
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Genera MP4 con la mia voce
              </>
            )}
          </button>

          {phase === "done" && downloadUrl && (
            <div className="mt-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-4" data-testid="voiceover-success">
              <p className="text-emerald-300 text-xs font-bold uppercase tracking-wider mb-2">
                MP4 pronto
              </p>
              <p className="text-white/80 text-sm mb-3">
                Il tuo video con voice-over è stato scaricato. Se serve, riscaricalo qui sotto.
              </p>
              <a
                href={downloadUrl}
                download="smartbooker-demo-90s-voce.mp4"
                data-testid="voiceover-redownload"
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-3 py-1.5"
              >
                <Download className="w-3 h-3" /> Scarica di nuovo
              </a>
            </div>
          )}

          {/* TIPS */}
          <div className="mt-7 pt-6 border-t border-white/10">
            <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider mb-3">
              Suggerimenti per la registrazione
            </p>
            <ul className="space-y-1.5 text-white/60 text-[12px] leading-relaxed">
              <li>• Durata target: <strong className="text-white/85">85-92 secondi</strong> (audio più corto → silenzio finale)</li>
              <li>• Microfono a 10-15 cm dalla bocca, ambiente con tappeti/tende</li>
              <li>• Usa lo script che ti ho preparato — è già calibrato sui timecode delle scene</li>
              <li>• Niente caffè o latte prima della registrazione</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoDemoVoiceOverPage;
